<template>
  <main class="mine-code-page safe-page">
    <AppNavBar :title="t('mine.mineCode')" background-color="#f5f5f5" />

    <section class="mine-code-card">
      <div class="mine-code-card__profile">
        <div class="mine-code-card__avatar">
          <app-image v-if="avatarUrl" class="mine-code-card__avatar-image" :src="avatarUrl" alt="" />
          <span v-else>{{ avatarText }}</span>
        </div>
        <div class="mine-code-card__profile-text">
          <div data-strong>{{ accountName }}</div>
          <span>{{ displayAddress }}</span>
        </div>
      </div>

      <div class="mine-code-card__qr-wrap">
        <div class="mine-code-card__qr-box">
          <app-image v-if="qrUrl" :src="qrUrl" alt="" class="mine-code-card__qr-image" />
          <div class="mine-code-card__qr-avatar">
            <app-image v-if="avatarUrl" class="mine-code-card__qr-avatar-image" :src="avatarUrl" alt="" />
            <span v-else>{{ avatarText }}</span>
          </div>
        </div>
      </div>

      <p class="mine-code-card__tip">{{ t('mine.scanAddFriend') }}</p>
    </section>

    <section class="mine-code-actions">
      <button type="button" class="mine-code-action" @click="goScan">
        <app-image src="@/assets/images/common_scancode_icon3.png" alt="" />
        <span>{{ t('home.scan') }}</span>
      </button>
      <button type="button" class="mine-code-action" :disabled="!walletAddress" @click="saveQrCode">
        <app-image class="mine-code-action__save" src="@/assets/images/commonImage/save_icon_blue.png" alt="" />
        <span>{{ t('invite.saveQrCode') }}</span>
      </button>
      <button type="button" class="mine-code-action" :disabled="!walletAddress" @click="copyAddress">
        <app-image class="mine-code-action__copy" src="@/assets/images/commonImage/copy_icon_gray.png" alt="" />
        <span>{{ t('wallet.copyAddress') }}</span>
      </button>
    </section>
  </main>
</template>

<script setup>
import { computed, watch, ref } from 'vue';
import QRCode from 'qrcode';
import { showFailToast, showSuccessToast } from 'vant';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import AppNavBar from '@/components/AppNavBar.vue';
import { useProfileStore } from '@/stores/profile';
import { resolveAvatarText, resolveAvatarUrl } from '@/utils/avatar';
import { shortAddress } from '@/utils/format';
import { useWalletScanner } from '@/composables/useWalletScanner';

const { t } = useI18n();
const router = useRouter();
const profileStore = useProfileStore();
const { openWalletScanner } = useWalletScanner();

const qrUrl = ref('');
const walletAddress = computed(() => String(profileStore.platformAddress || '').trim());
const accountName = computed(() => profileStore.currentAccount?.name || 'WinPay');
const avatarText = computed(() => resolveAvatarText(accountName.value));
const avatarUrl = computed(() => resolveAvatarUrl(profileStore.currentAccount?.avatar));
const displayAddress = computed(() => shortAddress(walletAddress.value) || '--');
const qrPayload = computed(() => (
  walletAddress.value ? `${walletAddress.value}&friend=true` : ''
));

watch(
  qrPayload,
  async (value) => {
    qrUrl.value = value
      ? await QRCode.toDataURL(value, {
        margin: 0,
        width: 320,
        errorCorrectionLevel: 'H'
      })
      : '';
  },
  { immediate: true }
);

function goScan() {
  openWalletScanner({
    back: '/mine/code',
    stateNamespace: 'home-code',
  });

}

async function copyAddress() {
  if (!walletAddress.value) {
    return;
  }

  try {
    await navigator.clipboard.writeText(walletAddress.value);
    showSuccessToast(t('wallet.copySuccess'));
  } catch (error) {
    showFailToast(t('wallet.copyFailed'));
  }
}

function drawRoundRect(ctx, x, y, width, height, radius) {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  ctx.lineTo(x + radius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
  });
}

async function saveQrCode() {
  if (!walletAddress.value || !qrUrl.value) {
    return;
  }

  try {
    const canvas = document.createElement('canvas');
    canvas.width = 600;
    canvas.height = 600;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('canvas unavailable');
    }

    ctx.fillStyle = '#d7e2ff';
    drawRoundRect(ctx, 0, 0, 600, 600, 30);
    ctx.fill();

    const qrImage = await loadImage(qrUrl.value);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(60, 60, 480, 480);
    ctx.drawImage(qrImage, 80, 80, 440, 440);

    const avatarX = 255;
    const avatarY = 255;
    const avatarSize = 90;
    const gradient = ctx.createLinearGradient(avatarX, avatarY, avatarX + avatarSize, avatarY + avatarSize);
    gradient.addColorStop(0, '#41a0ff');
    gradient.addColorStop(1, '#2dd4bf');
    ctx.fillStyle = gradient;
    drawRoundRect(ctx, avatarX, avatarY, avatarSize, avatarSize, 14);
    ctx.fill();

    if (avatarUrl.value) {
      try {
        const avatarImage = await loadImage(avatarUrl.value);
        ctx.save();
        drawRoundRect(ctx, avatarX, avatarY, avatarSize, avatarSize, 14);
        ctx.clip();
        ctx.drawImage(avatarImage, avatarX, avatarY, avatarSize, avatarSize);
        ctx.restore();
      } catch (error) {
        ctx.fillStyle = '#ffffff';
        ctx.font = '700 34px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(avatarText.value, avatarX + avatarSize / 2, avatarY + avatarSize / 2 + 2);
      }
    } else {
      ctx.fillStyle = '#ffffff';
      ctx.font = '700 34px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(avatarText.value, avatarX + avatarSize / 2, avatarY + avatarSize / 2 + 2);
    }

    const link = document.createElement('a');
    link.href = canvas.toDataURL('image/png');
    link.download = `winpay-mine-qrcode.png`;
    link.click();
    showSuccessToast(t('wallet.saveSuccess'));
  } catch (error) {
    showFailToast(t('wallet.saveFailed'));
  }
}
</script>

<style scoped lang="scss">
.mine-code-page {
  min-height: var(--app-viewport-height);
  padding: 0 14px 24px;
  background: #f5f5f5;
}

.mine-code-card {
  margin-top: 18px;
  padding: 24px 20px 18px;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 4px 18px rgba(15, 35, 95, 0.06);
}

.mine-code-card__profile {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-left: -20px;
}

.mine-code-card__avatar,
.mine-code-card__qr-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: 700;
  background: linear-gradient(135deg, #41a0ff 0%, #2dd4bf 100%);
}

.mine-code-card__avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  font-size: 18px;
  overflow: hidden;
}

.mine-code-card__avatar-image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.mine-code-card__profile-text {
  min-width: 0;
}

.mine-code-card__profile-text [data-strong] {
  display: block;
  color: #222;
  font-size: 20px;
  line-height: 28px;
  font-weight: 700;
}

.mine-code-card__profile-text span {
  display: block;
  margin-top: 6px;
  color: #666;
  font-size: 14px;
  line-height: 20px;
}

.mine-code-card__qr-wrap {
  display: flex;
  justify-content: center;
  margin-top: 20px;
}

.mine-code-card__qr-box {
  position: relative;
  width: 200px;
  height: 200px;
  border-radius: 10px;
  background: #d7e2ff;
}

.mine-code-card__qr-image {
  position: absolute;
  left: 10px;
  top: 10px;
  width: 180px;
  height: 180px;
  border-radius: 6px;
  background: #fff;
}

.mine-code-card__qr-avatar {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 30px;
  height: 30px;
  border-radius: 5px;
  transform: translate(-50%, -50%);
  font-size: 12px;
  box-shadow: 0 0 0 4px #fff;
  overflow: hidden;
}

.mine-code-card__qr-avatar-image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.mine-code-card__tip {
  margin: 10px 0 0;
  color: #2667ff;
  font-size: 14px;
  line-height: 20px;
  text-align: center;
}

.mine-code-actions {
  display: flex;
  gap: 12px;
  margin-top: 20px;
}

.mine-code-action {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 92px;
  padding: 16px 8px;
  border: 0;
  border-radius: 6px;
  background: #fff;
  color: #222;
  font-size: 14px;
  line-height: 20px;
  box-shadow: 0 4px 16px rgba(15, 35, 95, 0.04);
}

.mine-code-action .app-image {
  width: 22px;
  height: 22px;
  object-fit: contain;
}

.mine-code-action__copy {
  width: 20px !important;
  height: 20px !important;
}

.mine-code-action:disabled {
  opacity: 0.45;
}
</style>
